<template>
  <div class="rounded-md bg-white p-4">
    <div class="mb-4 space-y-4">
      <div class="grid grid-cols-2 gap-4">
        <InputField name="title" :label="t('invoiceTitle')" />
        <SelectField
          name="counterpartyType"
          :label="t('counterpartyType.label')"
          :options="counterpartyTypeOptions"
        />
        <TaxProcedureSelect
          class="col-start-1"
          :model-value="taxProcedure"
          :vat-id="counterpartyVatId"
          @update:model-value="handleTaxProcedureUpdate"
        />
        <AppCheckbox
          v-if="$can('crossAllocate', 'LedgerEntry')"
          v-model="showAllCounterpartyLedgerEntries"
          :label="t('showAllCounterpartyLedgerEntries')"
          class="col-start-2 self-end"
        />
      </div>
      <div class="border-b border-obsidian-300 pb-4">
        <LedgerEntrySelector
          :model-value="formValues.ledgerEntries ?? []"
          amount-to-select-key="amountToInvoice"
          :scope="{
            bookingId: booking.id,
            counterpartyType: formValues.counterpartyType,
            includeAllCounterparties: showAllCounterpartyLedgerEntries,
          }"
          :custom-filter="ledgerEntryTaxProcedureFilter"
          :disabled="isLoading"
          @update:model-value="setFieldValue('ledgerEntries', $event)"
        />
      </div>
      <CheckboxField name="sendInvoiceOnSave" :label="t('sendInvoiceOnSave')" />
    </div>
    <div class="flex justify-end gap-2">
      <CVButton
        variant="default"
        :is-loading="isLoading"
        :disabled="!isValidAmount"
        @click.prevent="handleSave"
      >
        {{ t('saveInvoice') }}
      </CVButton>
      <CVButton v-if="!props.hideCancelEditButton" @click.prevent="$emit('onCancel')">
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { Payment } from '@/entities/payments/payment.entity';
import LedgerEntrySelector from '../ledger-entries/LedgerEntrySelector.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { CounterpartyType, type LedgerEntry } from '@/entities/ledger/ledger-entry.entity';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import SelectField from '../SelectField.vue';
import InputField from '../InputField.vue';
import type { CreateInvoiceFromLedgerEntriesDto } from '@carvia/ros-client-types';
import { TaxProcedure } from '@carvia/ros-client-types';
import CheckboxField from '../CheckboxField.vue';
import AppCheckbox from '@/components/Checkbox.vue';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import { isLedgerEntryEligibleForInvoicing } from '@/utils/is-ledger-entry-eligible-for-invoicing';

const props = defineProps<{
  booking: Booking;
  payments: Payment[];
  hideCancelEditButton?: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', values: CreateInvoiceFromLedgerEntriesDto, sendInvoiceOnSave: boolean): void;
  (e: 'onCancel'): void;
}>();

const { t } = useI18n();
const showAllCounterpartyLedgerEntries = ref(false);

const {
  values: formValues,
  handleSubmit,
  setFieldValue,
} = useForm({
  initialValues: {
    title: '',
    counterpartyType: CounterpartyType.CUSTOMER,
    ledgerEntries: [],
    sendInvoiceOnSave: true,
  },
  validationSchema: toTypedSchema(
    yup.object({
      title: yup.string().required(),
      counterpartyType: yup
        .string()
        .oneOf([CounterpartyType.CUSTOMER, CounterpartyType.AGENCY])
        .required(),
      ledgerEntries: yup
        .array()
        .of(
          yup.object({
            id: yup.string().required(),
            customAmount: yup.number(),
          }),
        )
        .min(1)
        .required(),
      sendInvoiceOnSave: yup.boolean().required(),
    }),
  ),
});

const taxProcedure = ref<TaxProcedure>(props.booking.taxProcedure ?? TaxProcedure.REGULAR);

const handleTaxProcedureUpdate = (value: TaxProcedure) => {
  taxProcedure.value = value;
};

const counterpartyVatId = computed(() => {
  if (formValues.counterpartyType === CounterpartyType.AGENCY) {
    return props.booking.agency?.vatId;
  }
  return props.booking.customerData?.vatId;
});

const ledgerEntryTaxProcedureFilter = (entry: LedgerEntry) =>
  entry.taxProcedure === taxProcedure.value && isLedgerEntryEligibleForInvoicing(entry);

watch(taxProcedure, () => {
  setFieldValue('ledgerEntries', [], false);
});

const isValidAmount = computed(() => formValues.ledgerEntries!.length > 0);

const counterpartyTypeOptions = useCounterpartyTypeOptions([
  CounterpartyType.CUSTOMER,
  CounterpartyType.AGENCY,
]);

const handleSave = handleSubmit(async (values) => {
  emit(
    'onSubmit',
    {
      ...values,
      bookingId: props.booking.id,
      includeAllCounterparties: showAllCounterpartyLedgerEntries.value || undefined,
      taxProcedure: taxProcedure.value,
    } as CreateInvoiceFromLedgerEntriesDto,
    values.sendInvoiceOnSave,
  );
});
</script>

<i18n lang="json">
{
  "en": {
    "invoiceTitle": "Invoice Title",
    "selectPayment": "- Select Payment -",
    "payment": "Payment",
    "repayment": "Repayment",
    "sendInvoiceOnSave": "Automatically send Invoice to Customer after Saving",
    "saveInvoice": "Save Invoice",
    "saveAndFinalizeInvoice": "Save and finalize invoice",
    "cancel": "Cancel",
    "paymentsMade": "Payments made",
    "showAllCounterpartyLedgerEntries": "Show ledger entries of all counterparties"
  },
  "de": {
    "invoiceTitle": "Rechnungstitel",
    "selectPayment": "- Zahlung auswählen -",
    "payment": "Zahlung",
    "repayment": "Rückzahlung",
    "sendInvoiceOnSave": "Rechnung beim Speichern automatisch an Kunden senden",
    "saveInvoice": "Rechnung speichern",
    "saveAndFinalizeInvoice": "Rechnung speichern und abschließen",
    "cancel": "Abbrechen",
    "paymentsMade": "Getätigte Zahlungen",
    "showAllCounterpartyLedgerEntries": "Forderungseinträge aller Gegenparteien anzeigen"
  },
  "es": {
    "invoiceTitle": "Título de la factura",
    "selectPayment": "- Seleccionar pago -",
    "payment": "Pago",
    "repayment": "Reembolso",
    "sendInvoiceOnSave": "Enviar la factura al cliente automáticamente al guardar",
    "saveInvoice": "Guardar factura",
    "saveAndFinalizeInvoice": "Guardar y finalizar factura",
    "cancel": "Cancelar",
    "paymentsMade": "Pagos realizados",
    "showAllCounterpartyLedgerEntries": "Mostrar partidas de todas las contrapartes"
  }
}
</i18n>
